import { useEffect, useState, useRef, useCallback } from 'react';
import { getTalkSession } from '@talkjs/core';
import _ from 'lodash';
import Loader from '../../Loader';
import { Button } from '../../Button';
import { toast } from 'react-toastify';
import { RotateIcon, SendIcon } from '../../Svg';

export const CustomersConversations = ({ chatbot }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chatboxError, setChatboxError] = useState(null);
  const [session, setSession] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const subscriptionsRef = useRef({});

  // Get conversation ID from URL query params
  const getConversationIdFromUrl = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('conversation');
  }, []);

  // Update URL with conversation query param
  const updateUrlWithConversation = useCallback((conversationId) => {
    const url = new URL(window.location);
    if (conversationId) {
      url.searchParams.set('conversation', conversationId);
    } else {
      url.searchParams.delete('conversation');
    }
    window.history.replaceState({}, '', url);
  }, []);
  
  // Saved configuration values
  const savedConfig = {
    appId: _.get(chatbot, 'ui.talkjs.app_id', ''),
    secretKey: _.get(chatbot, 'ui.talkjs.secret_key', ''),
    enabled: _.get(chatbot, 'ui.talkjs.enabled', false),
    adminEmail: _.get(chatbot, 'ui.talkjs.admin_email', '')
  };

  // Check if configuration is properly saved and complete
  const isConfigurationSaved = savedConfig.enabled && savedConfig.appId && savedConfig.secretKey;

  // Generate JWT token for TalkJS authentication (frontend implementation for protected admin interface)
  const generateTalkJSToken = useCallback(async (userId, appId, secretKey) => {
    const { SignJWT } = await import('jose');
    const secret = new TextEncoder().encode(secretKey);
    
    const jwt = await new SignJWT({
      tokenType: 'user',
      iss: appId,
      sub: userId,
    })
    .setProtectedHeader({ alg: 'HS256' })
    .sign(secret);
    
    return jwt;
  }, []);

  // Initialize TalkJS session with authentication
  const createSession = useCallback(async () => {
    if (!savedConfig.appId || !savedConfig.enabled || !savedConfig.secretKey) return null;

    try {
      // Generate JWT token for authentication
      const token = await generateTalkJSToken('admin', savedConfig.appId, savedConfig.secretKey);
      
      // Create session with authentication
      const newSession = getTalkSession({
        appId: savedConfig.appId,
        userId: 'admin',
        token: token
      });
      
      // Set user information after session creation
      const userRef = newSession.user('admin');
      await userRef.createIfNotExists({
        name: 'Support Team',
        email: savedConfig.adminEmail,
        role: 'admin',
        custom: {
          userType: 'admin'
        }
      });
      
      return newSession;
    } catch (error) {
      console.error('Failed to create TalkJS session:', error);
      setError('Failed to initialize TalkJS session');
      return null;
    }
  }, [savedConfig.appId, savedConfig.enabled, savedConfig.secretKey, savedConfig.adminEmail, generateTalkJSToken]);

  // Cleanup session and subscriptions
  const destroySession = useCallback(() => {
    // Clean up all subscriptions with error handling
    Object.entries(subscriptionsRef.current).forEach(([key, sub]) => {
      if (sub && sub.unsubscribe) {
        try {
          sub.unsubscribe();
        } catch (error) {
          console.warn(`Error unsubscribing from ${key}:`, error);
        }
      }
    });
    subscriptionsRef.current = {};

    // Clear messages to prevent stale data
    setMessages([]);
    setChatboxError(null);

    // TalkJS Data API sessions don't need explicit destruction
    // Just clear the session state
    setSession(null);
  }, []);

  // Admin user creation removed - should be managed through TalkJS dashboard

  // Fetch all conversations with pagination to get complete list
  const fetchAllConversations = async (appId, secretKey) => {
    const allConversations = [];
    let hasMore = true;
    let startingAfter = null;
    
    while (hasMore) {
      try {
        let url = `https://api.talkjs.com/v1/${appId}/conversations?limit=30&orderBy=lastActivity&orderDirection=DESC`;
        if (startingAfter) {
          url += `&startingAfter=${startingAfter}`;
        }
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${secretKey}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`TalkJS API error: ${response.status}`);
        }

        const data = await response.json();
        const conversations = data.data || [];
        
        if (conversations.length === 0) {
          hasMore = false;
        } else {
          allConversations.push(...conversations);
          startingAfter = conversations[conversations.length - 1].id;
          
          // Limit to prevent infinite loops (max 1000 conversations)
          if (allConversations.length >= 1000) {
            hasMore = false;
          }
        }
      } catch (error) {
        console.error('Error fetching conversations page:', error);
        hasMore = false;
        throw error;
      }
    }
    
    return allConversations;
  };

  // Fetch conversations with specific config
  const fetchConversationsWithConfig = async (appId, secretKey) => {
    if (!appId || !secretKey) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Simply fetch conversations - no need to add admin to all of them
      const conversations = await fetchAllConversations(appId, secretKey);
      
      setConversations(conversations);
      
      // Reset to page 1 when conversations are refreshed
      setCurrentPage(1);
      
      if (conversations.length > 0) {
        // Check if there's a conversation ID in the URL query params
        const conversationIdFromUrl = getConversationIdFromUrl();
        
        if (conversationIdFromUrl) {
          // Find the conversation with the matching ID
          const targetConversation = conversations.find(conv => conv.id === conversationIdFromUrl);
          if (targetConversation) {
            setSelectedConversation(targetConversation);
          } else {
            // If conversation not found, select first one and update URL
            setSelectedConversation(conversations[0]);
            updateUrlWithConversation(conversations[0].id);
          }
        } else {
          // No URL param, select first conversation and update URL
          setSelectedConversation(conversations[0]);
          updateUrlWithConversation(conversations[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // This function was removed - admin access should be managed through TalkJS dashboard

  // Fetch conversations using saved config (for useEffect)
  const fetchConversations = async () => {
    return fetchConversationsWithConfig(savedConfig.appId, savedConfig.secretKey);
  };

  // Periodic refresh to keep conversations up to date
  useEffect(() => {
    if (!isConfigurationSaved) return;

    const intervalId = setInterval(async () => {
      try {
        const conversations = await fetchAllConversations(savedConfig.appId, savedConfig.secretKey);
        
        // Only update if conversation count changed significantly
        setConversations(prevConversations => {
          if (Math.abs(conversations.length - prevConversations.length) > 0) {
            return conversations;
          }
          return prevConversations;
        });
      } catch (error) {
        console.warn('Periodic refresh failed:', error);
      }
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [isConfigurationSaved, savedConfig.appId, savedConfig.secretKey]);

  // Setup message subscription for selected conversation
  const setupMessageSubscription = useCallback(async (talkSession, conversationId) => {
    if (!talkSession || !conversationId) return;

    // Prevent duplicate subscriptions for the same conversation
    if (subscriptionsRef.current.currentConversationId === conversationId) {
      return;
    }

    try {
      // Clean up previous subscriptions more thoroughly
      const cleanup = async () => {
        // Clear current conversation ID to prevent callbacks from firing
        const previousConversationId = subscriptionsRef.current.currentConversationId;
        subscriptionsRef.current.currentConversationId = null;
        
        if (subscriptionsRef.current.messages) {
          try {
            await subscriptionsRef.current.messages.unsubscribe();
          } catch (error) {
            console.warn('Error unsubscribing from previous message subscription:', error);
          }
          subscriptionsRef.current.messages = null;
        }
        if (subscriptionsRef.current.typing) {
          try {
            await subscriptionsRef.current.typing.unsubscribe();
          } catch (error) {
            console.warn('Error unsubscribing from typing subscription:', error);
          }
          subscriptionsRef.current.typing = null;
        }
        
        // Add longer delay if we switched conversations to ensure TalkJS cleanup
        if (previousConversationId && previousConversationId !== conversationId) {
          await new Promise(resolve => setTimeout(resolve, 300));
        } else {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      };
      
      await cleanup();
      
      // Admin access should be managed through TalkJS dashboard, not programmatically

      const conversationRef = talkSession.conversation(conversationId);

      // Set the current conversation ID BEFORE subscribing to prevent race condition
      subscriptionsRef.current.currentConversationId = conversationId;

      // Subscribe to messages with callback
      const messageSubscription = conversationRef.subscribeMessages((messageSnapshots) => {
        try {
          // Validate that we're still subscribed to the right conversation
          if (subscriptionsRef.current.currentConversationId !== conversationId) {
            // This can happen during conversation switching - just ignore silently
            return;
          }

          const messageList = Array.isArray(messageSnapshots) ? messageSnapshots : [];
          
          setMessages(messageList);
          
          // Scroll to bottom when new messages arrive
          setTimeout(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: 'smooth'
              });
            }
          }, 100);
          
          // Mark conversation as read - Data API may not support this method
          // Commenting out for now to prevent errors
          // conversationRef.markRead().catch(console.error);
          
        } catch (error) {
          console.error('Error in message subscription callback:', error);
          setChatboxError('Failed to process message updates');
        }
      });

      // Monitor message subscription for errors
      messageSubscription.terminated.then((terminalState) => {
        if (terminalState.type === 'error') {
          console.error('Message subscription error:', terminalState.error);
          setChatboxError('Failed to load messages');
        }
      });

      // Note: subscribeTyping might not be available in Data API
      // For now, we'll skip typing indicators
      
      subscriptionsRef.current.messages = messageSubscription;
      setChatboxError(null);

    } catch (error) {
      console.error('Error setting up message subscription:', error);
      setChatboxError('Failed to setup message subscription');
    }
  }, []);

  // Send message using Data API
  const sendMessage = useCallback(async () => {
    if (!session || !selectedConversation || !messageText.trim() || sending) return;
    
    // Prevent sending if conversation is not open
    if (!isConversationOpen(selectedConversation)) {
      toast.warning('Please mark the conversation as open before sending messages');
      return;
    }

    setSending(true);
    try {
      const conversationRef = session.conversation(selectedConversation.id);
      
      // Send message with text and custom metadata
      await conversationRef.send({
        text: messageText.trim(),
        custom: {
          sentBy: 'admin',
          timestamp: new Date().toISOString()
        }
      });
      
      setMessageText('');
      
      // Scroll to bottom after sending message
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  }, [session, selectedConversation, messageText, sending]);

  // Handle typing indicator (disabled for now with Data API)
  const handleTyping = useCallback(() => {
    // Typing indicators are not implemented yet with the Data API
    // This could be added later if needed
  }, []);

  // Handle manual refresh
  // Admin access management functions removed - should be handled through TalkJS dashboard

  const handleRefresh = async () => {
    if (refreshing || !isConfigurationSaved) return;
    
    setRefreshing(true);
    try {
      await fetchConversations();
      toast.success('Conversations refreshed successfully');
    } catch (error) {
      console.error('Error refreshing conversations:', error);
      toast.error('Failed to refresh conversations');
    } finally {
      setRefreshing(false);
    }
  };

  // Force sync function removed - admin access should be managed through TalkJS dashboard

  // Update conversation status (resolve/unresolve)
  const updateConversationStatus = async (conversationId, status) => {
    if (!savedConfig.appId || !savedConfig.secretKey) return;
    
    setUpdatingStatus(conversationId);
    
    try {
      const response = await fetch(`https://api.talkjs.com/v1/${savedConfig.appId}/conversations/${conversationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${savedConfig.secretKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          custom: {
            status: status,
            statusUpdatedAt: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update conversation status: ${response.status}`);
      }

      // If marking as resolved, automatically send a message using existing session
      if (status === 'resolved' && session && conversationId === selectedConversation?.id) {
        try {
          const conversationRef = session.conversation(conversationId);
          await conversationRef.send({
            text: "This issue has been marked as resolved by our support team. If you need further assistance, please don't hesitate to reach out again.",
            custom: {
              sentBy: 'admin',
              messageType: 'status_update',
              timestamp: new Date().toISOString()
            }
          });
        } catch (messageError) {
          console.warn('Failed to send automatic resolution message:', messageError);
          // Don't fail the whole operation if message sending fails
        }
      }

      // Update local state
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv.id === conversationId 
            ? { 
                ...conv, 
                custom: { 
                  ...conv.custom, 
                  status: status,
                  statusUpdatedAt: new Date().toISOString()
                } 
              }
            : conv
        )
      );

      // Update selected conversation if it's the one being updated
      // IMPORTANT: Don't clear or reset messages when updating status
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(prev => ({
          ...prev,
          custom: {
            ...prev.custom,
            status: status,
            statusUpdatedAt: new Date().toISOString()
          }
        }));
        // Keep the existing messages intact - don't trigger re-subscription
      }

      toast.success(`Conversation marked as ${status}`);
    } catch (error) {
      console.error('Error updating conversation status:', error);
      toast.error(`Failed to mark conversation as ${status}`);
    } finally {
      setUpdatingStatus(null);
    }
  };

  useEffect(() => {
    if (isConfigurationSaved) {
      const initializeSession = async () => {
        setLoading(true);
        setError(null);
        
        try {
          // Admin user should be created through TalkJS dashboard, not programmatically
          
          // Create TalkJS session for messaging functionality
          const newSession = await createSession();
          if (newSession) {
            setSession(newSession);
          }
          
          // Use REST API to fetch all conversations instead of subscription
          // This gives us full control over pagination and ensures we get ALL conversations
          await fetchConversationsWithConfig(savedConfig.appId, savedConfig.secretKey);
        } catch (error) {
          console.error('Error initializing TalkJS:', error);
          setError('Failed to initialize TalkJS');
          setLoading(false);
        }
      };

      initializeSession();
    } else {
      destroySession();
      setLoading(false);
    }

    return () => {
      destroySession();
    };
  }, [isConfigurationSaved, savedConfig.appId, savedConfig.secretKey, savedConfig.adminEmail, createSession, destroySession]);

  // Setup message subscription when conversation is selected
  useEffect(() => {
    if (session && selectedConversation) {
      // Only setup subscription if conversation ID actually changed
      // This prevents re-subscription when only conversation metadata (like status) changes
      if (subscriptionsRef.current.currentConversationId !== selectedConversation.id) {
        const setupSubscription = async () => {
          setMessages([]);
          setChatboxError(null);
          await setupMessageSubscription(session, selectedConversation.id);
        };
        setupSubscription().catch(error => {
          console.error('Error setting up message subscription:', error);
          setChatboxError('Failed to load conversation messages');
        });
      }
    }
  }, [session, selectedConversation?.id]); // Only depend on ID, not the entire object

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [messages]);

  // Handle Enter key for sending messages
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }, [sendMessage]);

  // Format message timestamp
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Extract text from TalkJS message structure
  const extractMessageText = (message) => {
    if (!message || typeof message !== 'object') return '';
    
    let messageText = '';
    
    // Use plaintext if available (TalkJS provides this)
    if (message.plaintext) {
      messageText = String(message.plaintext);
    }
    // Handle content array structure: [{type: "text", children: ["message text"]}]
    else if (Array.isArray(message.content)) {
      const textContent = message.content
        .filter(item => item && item.type === 'text' && Array.isArray(item.children))
        .map(item => item.children.join(' '))
        .join(' ');
      
      if (textContent) messageText = String(textContent);
    }
    // Fallback to other text fields
    else if (message.text) {
      messageText = String(message.text);
    }
    else {
      return 'Message';
    }
    
    // Remove "[Previous Support Response]:" prefix if present
    if (messageText.startsWith('[Previous Support Response]:')) {
      messageText = messageText.replace(/^\[Previous Support Response\]:\s*/, '').trim();
    }
    
    return messageText || 'Message';
  };

  // Check if message is a previous support response
  const isPreviousSupportResponse = (message) => {
    if (!message || typeof message !== 'object') return false;
    
    // Check plaintext field first
    if (message.plaintext && String(message.plaintext).startsWith('[Previous Support Response]:')) {
      return true;
    }
    
    // Check content array structure
    if (Array.isArray(message.content)) {
      const textContent = message.content
        .filter(item => item && item.type === 'text' && Array.isArray(item.children))
        .map(item => item.children.join(' '))
        .join(' ');
      
      if (textContent && textContent.startsWith('[Previous Support Response]:')) {
        return true;
      }
    }
    
    // Check text field
    if (message.text && String(message.text).startsWith('[Previous Support Response]:')) {
      return true;
    }
    
    return false;
  };

  // Render message content
  const renderMessage = (message, index) => {
    if (!message || typeof message !== 'object') return null;
    
    // Check if sender is admin - look in sender object OR if it's a previous support response
    const isFromAdmin = message.senderId === 'admin' || 
                       message.sender?.id === 'admin' ||
                       message.sender?.userId === 'admin' ||
                       message.from === 'admin' ||
                       message.userId === 'admin' ||
                       isPreviousSupportResponse(message);
                       
    const messageClass = `CustomersConversations__message ${isFromAdmin ? 'admin' : 'customer'}`;
    const messageId = message.id || `message-${index}`;
    const messageText = extractMessageText(message);
    
    return (
      <div key={messageId} className={messageClass}>
        <div className="CustomersConversations__message-content">
          <div className="CustomersConversations__message-text">
            {String(messageText)}
          </div>
          <div className="CustomersConversations__message-time">
            {String(formatMessageTime(message.createdAt) || '')}
          </div>
        </div>
      </div>
    );
  };


  const formatLastMessage = (conversation) => {
    if (!conversation || typeof conversation !== 'object') return 'No messages yet';
    if (!conversation.lastMessage) return 'No messages yet';
    
    const message = conversation.lastMessage;
    if (typeof message === 'string') return message;
    if (typeof message === 'object' && message) {
      return extractMessageText(message) || 'Message';
    }
    return 'Message';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return '';
    }
  };

  const isConversationResolved = (conversation) => {
    if (!conversation || typeof conversation !== 'object') return false;
    const status = conversation.custom?.status;
    return status === 'resolved' || status === 'ended_by_user';
  };

  const isConversationOpen = (conversation) => {
    if (!conversation || typeof conversation !== 'object') return false;
    const status = conversation.custom?.status;
    return !status || status === 'open';
  };

  const getStatusText = (conversation) => {
    if (!conversation || typeof conversation !== 'object') return 'Open';
    
    const status = conversation.custom?.status;
    
    switch (status) {
      case 'resolved':
        return 'Resolved';
      case 'ended_by_user':
        return 'Ended by user';
      case 'open':
        return 'Open';
      default:
        return 'Open';
    }
  };

  const getConversationSubject = (conversation) => {
    if (!conversation || typeof conversation !== 'object') return 'Customer Support';
    return conversation.subject || 'Customer Support';
  };

  const getConversationTimestamp = (conversation) => {
    if (!conversation || typeof conversation !== 'object') return '';
    return formatTimestamp(conversation.createdAt || conversation.lastMessage?.createdAt);
  };

  // Sort conversations by date (most recent first) and paginate
  const getSortedAndPaginatedConversations = () => {
    if (!Array.isArray(conversations)) return [];
    
    // Sort by most recent activity (lastMessage createdAt or conversation createdAt)
    const sorted = [...conversations].sort((a, b) => {
      const dateA = new Date(a.lastMessage?.createdAt || a.createdAt || 0);
      const dateB = new Date(b.lastMessage?.createdAt || b.createdAt || 0);
      return dateB.getTime() - dateA.getTime(); // Descending order
    });
    
    // Calculate pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    return sorted.slice(startIndex, endIndex);
  };

  // Calculate total pages
  const totalPages = Math.ceil(conversations.length / itemsPerPage);
  const paginatedConversations = getSortedAndPaginatedConversations();

  const StatusBadge = ({ conversation, size = 'small' }) => {
    const statusText = getStatusText(conversation);
    const status = conversation?.custom?.status || 'open';
    
    // Determine badge type for styling
    let badgeType;
    switch (status) {
      case 'resolved':
        badgeType = 'resolved';
        break;
      case 'ended_by_user':
        badgeType = 'ended';
        break;
      case 'open':
      default:
        badgeType = 'open';
        break;
    }
    
    const badgeClass = `CustomersConversations__status-badge CustomersConversations__status-badge--${size} CustomersConversations__status-badge--${badgeType}`;
    const dotClass = `CustomersConversations__status-dot CustomersConversations__status-dot--${badgeType}`;

    return (
      <span className={badgeClass}>
        <span className={dotClass} />
        {String(statusText || 'Open')}
      </span>
    );
  };

  if (!isConfigurationSaved) {
    return (
      <>
        <h3 className='RagChatbotDetailPage__section-title'>Customer Conversations</h3>
        <div className='RagChatbotDetailPage__section-content'>
          <div className='RagChatbotDetailPage__empty-state'>
            Please configure TalkJS settings in the Configuration tab to view customer conversations.
          </div>
        </div>
      </>
    );
  }

  if (!session) {
    return (
      <>
        <h3 className='RagChatbotDetailPage__section-title'>Customer Conversations</h3>
        <div className='RagChatbotDetailPage__section-content'>
          <div className='RagChatbotDetailPage__empty-state'>
            <Loader size='large' color='grey' />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className='CustomersConversations__header'>
        <h3 className='RagChatbotDetailPage__section-title'>Customer Conversations</h3>
        <div className='CustomersConversations__header-buttons'>
          <button
            className={`CustomersConversations__refresh-button ${refreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing || !isConfigurationSaved}
            title="Refresh conversations"
          >
            <RotateIcon />
          </button>
        </div>
      </div>
      <div className='RagChatbotDetailPage__section-content RagChatbotDetailPage__section-content--full-width'>
        <div className='CustomersConversations__container'>
          <div className='CustomersConversations__conversations-list'>
            {loading ? (
              <div className='RagChatbotDetailPage__empty-state'>
                <Loader size='large' color='grey' />
              </div>
            ) : error ? (
              <div className='RagChatbotDetailPage__empty-state'>
                <div className='CustomersConversations__error-message'>
                  Error loading conversations: {error}
                </div>
                <button 
                  onClick={fetchConversations}
                  className='CustomersConversations__retry-button'
                >
                  Retry
                </button>
              </div>
            ) : conversations.length === 0 ? (
              <div className='RagChatbotDetailPage__empty-state'>
                No conversations yet. When customers start chatting with your bot and escalate to human support, they'll appear here.
              </div>
            ) : (
              <div className='RagChatbotDetailPage__conversations'>
                {paginatedConversations
                  .filter(conversation => conversation && typeof conversation === 'object' && conversation.id)
                  .map((conversation) => (
                    <div
                      key={conversation.id}
                      className={`CustomersConversations__conversation-item ${
                        selectedConversation?.id === conversation.id ? 'selected' : ''
                      } ${isConversationResolved(conversation) ? 'resolved' : ''}`}
                      onClick={() => {
                        setChatboxError(null);
                        setSelectedConversation(conversation);
                        
                        // Print conversation.custom for debugging
                        console.log('conversation.custom:', conversation.custom);
                        
                        // Update URL with selected conversation ID
                        updateUrlWithConversation(conversation.id);
                      }}
                    >
                      <div className='CustomersConversations__conversation-header'>
                        <div className='CustomersConversations__conversation-subject'>
                          {String(getConversationSubject(conversation) || 'Customer Support')}
                        </div>
                        <StatusBadge conversation={conversation} size="small" />
                      </div>
                      <div className='CustomersConversations__conversation-preview'>
                        {String(formatLastMessage(conversation) || 'No messages yet')}
                      </div>
                      <div className='CustomersConversations__conversation-time'>
                        {String(getConversationTimestamp(conversation) || '')}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className='CustomersConversations__chat-view'>
            {selectedConversation ? (
              chatboxError ? (
                <div className='RagChatbotDetailPage__no-conversation-selected'>
                  <div className='CustomersConversations__error-message'>
                    Error loading chat: {chatboxError}
                  </div>
                  <button 
                    onClick={() => {
                      setChatboxError(null);
                      setSelectedConversation(null);
                      setTimeout(() => setSelectedConversation(selectedConversation), 100);
                    }}
                    className='CustomersConversations__retry-button'
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className='CustomersConversations__chat-container'>
                  <div className='CustomersConversations__chat-header'>
                    <div className='CustomersConversations__chat-info'>
                      <StatusBadge conversation={selectedConversation} size="medium" />
                    </div>
                    <div className='RagChatbotDetailPage__chat-actions'>
                      <Button
                        color={isConversationResolved(selectedConversation) ? "grey" : "green"}
                        title={isConversationResolved(selectedConversation) ? "Mark as Open" : "Mark as Resolved"}
                        onClick={() => updateConversationStatus(
                          selectedConversation.id, 
                          isConversationResolved(selectedConversation) ? 'open' : 'resolved'
                        )}
                        loading={updatingStatus === selectedConversation.id}
                        disabled={updatingStatus === selectedConversation.id}
                        size="small"
                      />
                    </div>
                  </div>
                  <div className='CustomersConversations__custom-chat'>
                    <div className='CustomersConversations__messages' ref={messagesContainerRef}>
                      {messages && Array.isArray(messages) ? 
                        messages
                          .filter(message => message && typeof message === 'object')
                          .sort((a, b) => {
                            // Sort by timestamp in ascending order (oldest first, newest last)
                            const dateA = new Date(a.createdAt || 0);
                            const dateB = new Date(b.createdAt || 0);
                            return dateA.getTime() - dateB.getTime();
                          })
                          .map((message, index) => renderMessage(message, index))
                        : null
                      }
                      <div ref={messagesEndRef} />
                    </div>
                    <div className='CustomersConversations__message-input'>
                      <input
                        type="text"
                        value={messageText}
                        onChange={(e) => {
                          setMessageText(e.target.value);
                          handleTyping();
                        }}
                        onKeyDown={handleKeyPress}
                        placeholder={isConversationOpen(selectedConversation) ? "Type a message..." : "Conversation must be open to send messages"}
                        disabled={sending || !isConversationOpen(selectedConversation)}
                        className='CustomersConversations__input'
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!messageText.trim() || sending || !isConversationOpen(selectedConversation)}
                        className='CustomersConversations__send-button'
                      >
                        <SendIcon />
                      </button>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className='CustomersConversations__no-conversation-selected'>
                Select a conversation to view and respond to messages
              </div>
            )}
          </div>
        </div>
        
        {/* Pagination Controls - Moved outside and below the 2 panels */}
        {conversations.length > itemsPerPage && (
          <div className='CustomersConversations__pagination'>
            <button
              className='CustomersConversations__pagination-btn'
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            <span className='CustomersConversations__pagination-info'>
              Page {currentPage} of {totalPages} ({conversations.length} total)
            </span>
            
            <button
              className='CustomersConversations__pagination-btn'
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};